import type { SourceId } from "@workspace/api-client-react";
import type { Folder } from "@/pages/inbox/types";
import { formatFolderLabel } from "@/pages/inbox/utils";

// Pure logic behind the pinned view tabs (the bar along the bottom of the
// app). Kept out of the components so the two rules that actually decide
// whether a pin behaves correctly — what part of the URL is pinnable, and
// what a tab is called — are in one place and unit-testable.

// Query params that describe a *transient selection layered on top of* a
// screen rather than the screen itself: which message is open in the reading
// pane, which task's detail sheet is showing. Both are per-visit state — you
// pin "the Acme folder", not "the Acme folder with that one email open" —
// and a pinned message id also goes stale the moment the message is deleted.
// Everything else in the query string is part of the screen's identity and
// is kept: ?q= is what makes a saved search a distinct screen, ?tab= is which
// settings tab.
const TRANSIENT_PARAMS = ["message", "task"];

// Mirrors the server's isSafeAppPath (routes/pinned-views.ts). Duplicated
// rather than shared because the stored path is fed into client-side
// navigation *here* — the client is the place the value is dangerous, so it
// re-checks rather than trusting a response body, and the server still
// validates on write so a bad row can't be created in the first place.
export function isSafeAppPath(path: string): boolean {
  if (path.length === 0 || path.length > 512) return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.includes("\\")) return false;
  if (/[\s\u0000-\u001f\u007f]/.test(path)) return false;
  return true;
}

// The exact string a pin stores for "where I am now": the current path plus
// whichever query params are part of the screen's identity, in the order the
// URL already had them. Two visits to the same screen must produce the same
// string — that's what makes the pin control a reliable toggle and lets the
// (userId, path) unique index do its job.
export function pinnablePath(location: string, search: string): string {
  const params = new URLSearchParams(search);
  for (const key of TRANSIENT_PARAMS) params.delete(key);
  const qs = params.toString();
  return qs ? `${location}?${qs}` : location;
}

// Everything the label resolver needs to name a route. Both lists are the
// same data the nav already has loaded, so resolving a label costs no extra
// request.
export type RouteLabelContext = {
  folders: Folder[];
  docs: { id: number; title: string }[];
};

const STATIC_LABELS: Record<string, string> = {
  "/": "Inbox",
  "/tasks": "All tasks",
  "/docs": "Docs",
  "/calendar": "Calendar",
  "/analytics": "Analytics",
  "/ai-chat": "AI Chat",
  "/search": "Search",
  "/settings": "Settings",
};

// Mirrors UnifiedSettings' own TABS. A tab missing from here still resolves
// (the screen names itself "Settings"), so a new settings tab is pinnable
// before this catches up — it just isn't named as precisely.
const SETTINGS_TAB_LABELS: Record<string, string> = {
  preferences: "Preferences",
  email: "Email",
  calendar: "Calendar",
  wellbeing: "Wellbeing",
  rules: "Rules",
  connections: "Connections",
  account: "Account",
};

// Inverse of Inbox.tsx's goToFolder(): a route back to the internal
// `list:12` / `slack_channel:general` folder-key shape, so a label comes from
// the same formatFolderLabel() the nav itself uses rather than a second,
// drifting naming scheme.
function folderKeyForPath(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length !== 2) return null;
  const [head, raw] = segments;
  const value = decodeURIComponent(raw!);
  if (head === "list") return `list:${value}`;
  if (head === "slack") return `slack_channel:${value}`;
  if (head === "teams") return `teams_conversation:${value}`;
  if (head === "chat") return `native_channel:${value}`;
  if (head === "folder") return value;
  return null;
}

// The one route-to-label resolver. Used both to auto-name a tab at pin time
// and to re-derive an auto-named tab's label on every render — which is what
// makes renaming a list or folder rename its tab too, rather than leaving a
// stale name behind. A user-renamed tab (isCustomLabel) never comes through
// here.
//
// Returns null when the route can't be named — an unknown path, or a list/
// doc that has since been deleted. Callers fall back to the label stored on
// the pin, so a tab pointing at something gone degrades to a stale name
// instead of an empty one.
export function resolveRouteLabel(path: string, ctx: RouteLabelContext): string | null {
  const [pathname, search = ""] = path.split("?");
  if (!pathname) return null;

  if (pathname === "/search") {
    const query = new URLSearchParams(search).get("q")?.trim();
    return query ? `Search: ${query}` : STATIC_LABELS["/search"]!;
  }

  if (pathname === "/settings") {
    const tab = new URLSearchParams(search).get("tab");
    const tabLabel = tab ? SETTINGS_TAB_LABELS[tab] : undefined;
    return tabLabel ? `Settings — ${tabLabel}` : STATIC_LABELS["/settings"]!;
  }

  const staticLabel = STATIC_LABELS[pathname];
  if (staticLabel) return staticLabel;

  const docMatch = /^\/docs\/(\d+)$/.exec(pathname);
  if (docMatch) {
    const doc = ctx.docs.find((d) => d.id === Number(docMatch[1]));
    // An untitled doc has an empty title, not a placeholder one — naming the
    // tab after the empty string would render a tab with no visible name.
    return doc ? (doc.title.trim() || "Untitled doc") : null;
  }

  const key = folderKeyForPath(pathname);
  if (key) {
    const folder = ctx.folders.find((f) => f.key === key);
    return folder ? formatFolderLabel(folder) : null;
  }

  return null;
}

// What a tab actually shows: the user's own name if they set one, otherwise
// the freshly-resolved route label, otherwise the name captured when it was
// pinned.
export function tabLabel(
  pin: { path: string; label: string; isCustomLabel: boolean },
  ctx: RouteLabelContext,
): string {
  if (pin.isCustomLabel) return pin.label;
  return resolveRouteLabel(pin.path, ctx) ?? pin.label;
}

// ─── Icons ───────────────────────────────────────────────────────────────────
// A tab carries the same icon its nav item does, so the bar reads as a row of
// familiar things rather than a row of words. Resolution returns an *identity*,
// not a React node, for two reasons: this module stays pure and unit-testable
// (its tests run in node, with no DOM), and the actual glyphs stay in one place
// in the bar component next to the sizing they need.
//
// "source" defers to SOURCE_REGISTRY (docs/standards/09-integrations.md,
// INT-11) rather than hard-coding a second provider→logo mapping here.
export type RouteIcon =
  | { kind: "source"; source: SourceId }
  | { kind: "emoji"; emoji: string }
  | { kind: "glyph"; glyph: RouteGlyph };

export type RouteGlyph =
  | "inbox" | "tasks" | "docs" | "doc" | "calendar" | "analytics" | "ai"
  | "search" | "settings" | "preferences" | "wellbeing" | "rules"
  | "connections" | "account" | "mail" | "sent" | "drafts" | "archive"
  | "trash" | "chat" | "list" | "ado" | "folder";

const STATIC_GLYPHS: Record<string, RouteGlyph> = {
  "/": "inbox",
  "/tasks": "tasks",
  "/docs": "docs",
  "/calendar": "calendar",
  "/analytics": "analytics",
  "/ai-chat": "ai",
  "/search": "search",
  "/settings": "settings",
};

// Each Settings tab gets its own tab icon, matching UnifiedSettings' TABS —
// otherwise two pinned settings tabs are indistinguishable at a glance.
const SETTINGS_TAB_GLYPHS: Record<string, RouteGlyph> = {
  preferences: "preferences",
  email: "mail",
  calendar: "calendar",
  wellbeing: "wellbeing",
  rules: "rules",
  connections: "connections",
  account: "account",
};

// The five folder concepts the nav itself gives a dedicated icon (see
// iconForProviderFolder in FolderNav.tsx). Matched on the normalized display
// label, the same signal the nav matches on.
const PROVIDER_FOLDER_GLYPHS: Record<string, RouteGlyph> = {
  Inbox: "inbox",
  Sent: "sent",
  Drafts: "drafts",
  Archive: "archive",
  Trash: "trash",
};

function iconForFolder(folder: Folder): RouteIcon {
  switch (folder.kind) {
    case "list":
      // A list's own emoji wins — the user chose it, and it's how they
      // recognise that list everywhere else in the app.
      if (folder.icon) return { kind: "emoji", emoji: folder.icon };
      if (folder.source === "jira") return { kind: "source", source: "jira" };
      if (folder.source === "google_tasks") return { kind: "source", source: "google_tasks" };
      // Azure DevOps has a brand mark but isn't a SourceId (it arrives as a
      // list source, not a message source), so it needs its own glyph.
      if (folder.source === "ado") return { kind: "glyph", glyph: "ado" };
      return { kind: "glyph", glyph: "list" };
    case "provider_folder": {
      const concept = PROVIDER_FOLDER_GLYPHS[folder.label];
      // A folder that maps to a known concept shows that concept; anything
      // else (a custom Gmail label, Outlook's Junk Email) shows which account
      // it belongs to instead, which the tab's name often doesn't say.
      if (concept) return { kind: "glyph", glyph: concept };
      if (folder.source === "gmail") return { kind: "source", source: "gmail" };
      if (folder.source === "outlook") return { kind: "source", source: "outlook" };
      return { kind: "glyph", glyph: "folder" };
    }
    case "slack_channel":
      return { kind: "source", source: "slack" };
    case "teams_conversation":
      return { kind: "source", source: "teams" };
    case "native_channel":
      return { kind: "glyph", glyph: "chat" };
    case "sent":
      return { kind: "glyph", glyph: "sent" };
    case "domain":
      // A sender-domain folder spans whatever accounts mail arrived on, so
      // there's no single provider to brand it with.
      return { kind: "glyph", glyph: "mail" };
  }
}

// The icon half of the route resolver. Falls back to a plain folder glyph
// rather than nothing: a tab with no icon at all would sit visibly out of line
// with its neighbours, and a pin can outlive the thing it points at.
export function resolveRouteIcon(path: string, ctx: RouteLabelContext): RouteIcon {
  const [pathname, search = ""] = path.split("?");
  if (!pathname) return { kind: "glyph", glyph: "folder" };

  if (pathname === "/settings") {
    const tab = new URLSearchParams(search).get("tab");
    const glyph = (tab && SETTINGS_TAB_GLYPHS[tab]) || "settings";
    return { kind: "glyph", glyph };
  }

  const staticGlyph = STATIC_GLYPHS[pathname];
  if (staticGlyph) return { kind: "glyph", glyph: staticGlyph };

  if (/^\/docs\/\d+$/.test(pathname)) return { kind: "glyph", glyph: "doc" };

  const key = folderKeyForPath(pathname);
  if (key) {
    const folder = ctx.folders.find((f) => f.key === key);
    if (folder) return iconForFolder(folder);
  }

  return { kind: "glyph", glyph: "folder" };
}
